import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { adminApi } from '../api/endpoints';

export default function AdminData() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [audit, setAudit] = useState(null);
  const [auditErr, setAuditErr] = useState('');
  const [file, setFile] = useState(null);
  const [defaults, setDefaults] = useState(null);

  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    adminApi.onecDefaults()
      .then((r) => setDefaults(r.data))
      .catch(() => {});
  }, [user]);

  if (!user || user.role !== 'admin') return null;

  const fileName = defaults?.file_name || '—';
  const expectedTotal = defaults?.expected_comma_total != null
    ? defaults.expected_comma_total.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

  const runImport = async (useServerFile) => {
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('use_server_file', useServerFile ? 'true' : 'false');
      if (!useServerFile && file) fd.append('file', file);
      const r = await adminApi.import1c(fd);
      setResult(r.data);
      loadAudit();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map((x) => x.msg || JSON.stringify(x)).join(' ')
        : (typeof d === 'string' ? d : (e.message || 'Ошибка'));
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadAudit = async () => {
    setAuditErr('');
    setAudit(null);
    try {
      const r = await adminApi.onecRevenueAudit({});
      setAudit(r.data);
    } catch (e) {
      setAuditErr(e.response?.data?.detail || e.message || 'Ошибка');
    }
  };

  const runBackfill = async () => {
    setAuditErr('');
    try {
      const r = await adminApi.backfill1cDates();
      setResult({ backfill_only: true, ...r.data });
      await loadAudit();
    } catch (e) {
      setAuditErr(e.response?.data?.detail || e.message || 'Ошибка');
    }
  };

  return (
    <div>
      <h1 style={styles.h1}>Данные (админ)</h1>
      <p style={styles.lead}>
        Полная замена выручки 1С в базе из отчёта «Валовая прибыль». Контроль суммы по товарным строкам —
        как в 1С (файл <code style={styles.code}>{fileName}</code>, ожидаемый итог {expectedTotal}).
      </p>

      <div style={styles.card}>
        <h2 style={styles.h2}>Импорт с сервера</h2>
        <p style={styles.muted}>
          Файл должен лежать на сервере в <code style={styles.code}>BazaDann/{fileName}</code> (как в Docker volume).
        </p>
        <button
          type="button"
          disabled={loading}
          onClick={() => runImport(true)}
          style={styles.btnPrimary}
        >
          {loading ? 'Импорт…' : 'Загрузить из BazaDann на сервере'}
        </button>
      </div>

      <div style={styles.card}>
        <h2 style={styles.h2}>Импорт с компьютера</h2>
        <input
          type="file"
          accept=".xlsx"
          style={styles.file}
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
        <button
          type="button"
          disabled={loading || !file}
          onClick={() => runImport(false)}
          style={styles.btnSecondary}
        >
          {loading ? 'Импорт…' : 'Загрузить выбранный .xlsx'}
        </button>
      </div>

      <div style={styles.card}>
        <h2 style={styles.h2}>Сверка с БД</h2>
        <p style={styles.muted}>
          Сумма выручки 1С в SQLite и строки без даты (не попадают в KPI по периоду, пока дата не подставлена).
        </p>
        <button type="button" onClick={loadAudit} style={styles.btnSecondary}>
          Загрузить сводку
        </button>
        <button type="button" onClick={runBackfill} style={{ ...styles.btnSecondary, marginLeft: 10 }}>
          Подставить даты (backfill)
        </button>
        {auditErr && <div style={{ ...styles.error, marginTop: 12 }}>{auditErr}</div>}
        {audit && (
          <div style={{ marginTop: 14, fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
            <div>Всего выручка 1С в БД: <strong style={{ color: 'var(--text-primary)' }}>{audit.total_all_revenue?.toLocaleString('ru-RU', { minimumFractionDigits: 2 })}</strong></div>
            <div>Строк с date = NULL: <strong>{audit.null_date_rows}</strong>, сумма: {audit.null_date_revenue?.toLocaleString('ru-RU', { minimumFractionDigits: 2 })}</div>
          </div>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {result && (
        <div style={styles.ok}>
          {!result.backfill_only && (
            <>
              <div>Готово: импортировано записей — <strong>{result.imported}</strong></div>
              <div>Удалено старых строк 1С — <strong>{result.deleted_1c_rows}</strong></div>
              <div>Проверка суммы (товарные строки): {result.comma_total_verified?.toLocaleString('ru-RU', { minimumFractionDigits: 2 })}</div>
            </>
          )}
          {result.backfill_only && (
            <div>Подставлено дат для строк: <strong>{result.backfilled_rows}</strong></div>
          )}
          {!result.backfill_only && (result.backfilled_null_dates > 0) && (
            <div>Даты подставлены для строк с NULL: <strong>{result.backfilled_null_dates}</strong></div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  h1: { color: 'var(--text-primary)', fontSize: 22, fontWeight: 700, marginBottom: 12, letterSpacing: -0.3 },
  h2: { color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, marginBottom: 10 },
  lead: { color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.5, marginBottom: 22, maxWidth: 720 },
  code: { fontSize: 12, color: 'var(--accent)' },
  card: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    padding: 20,
    marginBottom: 18,
    maxWidth: 560,
  },
  muted: { color: 'var(--text-muted)', fontSize: 13, marginBottom: 14 },
  btnPrimary: {
    background: 'var(--accent)',
    color: '#0a0e14',
    border: 'none',
    borderRadius: 10,
    padding: '12px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnSecondary: {
    background: 'var(--accent-dim)',
    color: 'var(--accent)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '12px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 12,
  },
  file: { display: 'block', marginBottom: 8, fontSize: 13, color: 'var(--text-secondary)' },
  error: {
    color: 'var(--danger)',
    background: 'rgba(248,113,113,.1)',
    border: '1px solid rgba(248,113,113,.2)',
    padding: '12px 16px',
    borderRadius: 10,
    marginTop: 16,
    fontSize: 13,
    maxWidth: 560,
  },
  ok: {
    color: 'var(--text-primary)',
    background: 'rgba(34,211,238,.08)',
    border: '1px solid rgba(34,211,238,.2)',
    padding: '14px 18px',
    borderRadius: 10,
    marginTop: 16,
    fontSize: 14,
    lineHeight: 1.6,
    maxWidth: 560,
  },
};
